import React, { useEffect, useRef, useState } from 'react';
import { COLOR_SCHEMES, getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import TmuaPearsonShell, {
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
} from './TmuaPearsonShell';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { isPearsonTimeRemainingWarning, tickPearsonLocalCountdown } from '../utils/pearsonTimerChrome';

/**
 * UCAT rules / fitness-to-test screen shown after the NDA screen.
 * Copy matches the official Practice Test reference. Includes a 2-minute
 * countdown that auto-advances (same as Next). Colours use IVORI /
 * COLOR_SCHEMES rather than Pearson blue.
 */

const RULES_SECONDS = 120;

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** Pearson-chrome branch: TmuaPearsonShell wrapper, Next-only footer (no Help, no End Exam per harvest 03-rules). */
const UcatPearsonRulesScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'pearsonDefault',
  setColorScheme,
  onNext,
  onEndExam,
}) => {
  const T = TMUA_PEARSON_TOKENS;
  const [secondsLeft, setSecondsLeft] = useState(RULES_SECONDS);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  const advancedRef = useRef(false);

  const advance = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    onNextRef.current?.();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = tickPearsonLocalCountdown(prev, { helpOpen: false });
        if (next <= 0) {
          clearInterval(interval);
          advance();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTmuaPearsonAccessKeys(
    { nextAvailable: true, endExamAvailable: false },
    { onNext: advance }
  );

  const warn = isPearsonTimeRemainingWarning(secondsLeft, { enabled: true });

  return (
    <TmuaPearsonShell
      examName="UCAT"
      title={paperTitle}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      colorSchemeLabel={getPearsonColorSchemeDropdownLabel('UCAT')}
      onBackToSelection={onEndExam}
      timeRemainingLabel={(
        <span style={{ color: warn ? (colorScheme === 'ivori' ? '#B5532E' : '#B45309') : 'inherit' }}>{formatCountdown(secondsLeft)}</span>
      )}
      footerLeft={null}
      footerRight={(
        <TmuaPearsonFooterButton variant="chrome" onClick={advance} icon={<NextIcon color="#FFFFFF" />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div className="tmua-pearson-instruction-copy" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <div style={{ maxWidth: '920px' }}>
          <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 16 }}>UCAT RULES AND TEST PROBLEMS</p>
          <p style={{ margin: '0 0 12px', fontWeight: 700 }}>
            You have 2 minutes to read this screen. Please take the time to read this important information about test
            rules and what to do if you have a problem during your test.
          </p>
          <p style={{ margin: '0 0 8px', fontWeight: 700 }}>Fitness to Test</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 22 }}>
            <li style={{ marginBottom: 6 }}>
              Starting this practice test means you are declaring yourself fit to test. If you are not fit to test,
              please stop now.
            </li>
          </ul>
          <p style={{ margin: '0 0 8px', fontWeight: 700 }}>Problems during testing</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 22 }}>
            <li style={{ marginBottom: 6 }}>
              If you experience a problem with this practice sitting, use the Help button on the following screens for
              guidance, or simply retake the practice sitting.
            </li>
          </ul>
          <p style={{ margin: 0 }}>
            Please click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button to proceed before the time on this screen expires.
          </p>
        </div>
      </div>
    </TmuaPearsonShell>
  );
};

const UcatCreamRulesScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'ivori',
  setColorScheme,
  onNext,
}) => {
  const schemeOptions = getColorSchemesForExam('UCAT');
  const scheme = schemeOptions[colorScheme] || COLOR_SCHEMES.ivori;
  const ink = scheme.text || 'var(--iv-head)';
  const divider = colorScheme !== 'ivori' ? scheme.border : 'var(--iv-line)';
  const [secondsLeft, setSecondsLeft] = useState(RULES_SECONDS);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  const bodyText = {
    fontSize: '15px',
    lineHeight: 1.6,
    color: ink,
    margin: '0 0 14px',
    fontFamily: 'var(--iv-font-body)',
  };

  const sectionHeading = {
    fontSize: '15px',
    lineHeight: 1.6,
    color: ink,
    margin: '0 0 8px',
    fontWeight: 700,
    fontFamily: 'var(--iv-font-body)',
  };

  const bulletList = {
    margin: '0 0 16px',
    paddingLeft: '22px',
    fontSize: '15px',
    lineHeight: 1.6,
    color: ink,
    fontFamily: 'var(--iv-font-body)',
    listStyleType: 'disc',
    listStylePosition: 'outside',
  };

  const bulletItem = {
    marginBottom: '6px',
    display: 'list-item',
    listStyleType: 'disc',
    color: ink,
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onNextRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.isComposing) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'select' || tag === 'input' || tag === 'textarea') return;
      if (event.ctrlKey || event.metaKey) return;

      const isN = event.key === 'n' || event.key === 'N';
      if (!isN) return;
      event.preventDefault();
      onNext?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNext]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: scheme.body,
        color: ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--iv-font-body)',
      }}
    >
      {/* Title bar with countdown */}
      <div
        style={{
          padding: '12px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 700, color: ink }}>
          {paperTitle}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 700,
            color: ink,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
          Time Remaining {formatCountdown(secondsLeft)}
        </span>
      </div>

      {/* Colour scheme bar */}
      <div
        style={{
          padding: '8px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <select
          value={schemeOptions[colorScheme] ? colorScheme : 'ivori'}
          onChange={(e) => setColorScheme?.(e.target.value)}
          aria-label="Colour Scheme"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: ink,
            fontFamily: 'var(--iv-font-body)',
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
            minWidth: '160px',
          }}
        >
          {Object.entries(schemeOptions).map(([key, s]) => (
            <option key={key} value={key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 40px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ maxWidth: '920px' }}>
          <p style={{ ...sectionHeading, fontSize: '16px', marginBottom: '12px' }}>
            UCAT RULES AND TEST PROBLEMS
          </p>
          <p style={{ ...bodyText, fontWeight: 700 }}>
            You have 2 minutes to read this screen. Please take the time to read this important information about test rules and what to do if you have a problem during your test.
          </p>

          <p style={sectionHeading}>Fitness to Test Declaration</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              In attending to test you are declaring you are <strong>Fit to Test.</strong>
            </li>
            <li style={bulletItem}>
              If you are not fit to test due to illness/injury or personal circumstances that may affect your test performance, please stop your test now.
            </li>
            <li style={bulletItem}>
              You must then contact the UCAT Office as soon as possible for advice and support with testing.
            </li>
            <li style={bulletItem}>
              If you choose to test against this advice, UCAT will not normally take any action.
            </li>
          </ul>

          <p style={sectionHeading}>Problems with your health or wellbeing during testing:</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              If you <strong>become unwell</strong> during your test please raise your hand immediately to notify the invigilator.
            </li>
            <li style={bulletItem}>
              If continuing would affect your performance, you should ask to end your test. Staff cannot make this decision for you.
            </li>
            <li style={bulletItem}>
              Ensure you obtain an incident case number before you leave the test centre.
            </li>
            <li style={bulletItem}>
              You must then contact the UCAT Office as soon as possible to arrange a new test booking.
            </li>
            <li style={bulletItem}>
              If you choose to continue testing, UCAT will not normally take any action.
            </li>
          </ul>

          <p style={sectionHeading}>Problems with your test or environment:</p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              If you experience hardware/software problems, excessive noise disturbance or other distractions, please raise your hand immediately to inform the invigilator so the problem can be addressed and the incident recorded.
            </li>
            <li style={bulletItem}>
              <strong>If a problem is not reported during your test, UCAT will not normally take any action.</strong>
            </li>
            <li style={bulletItem}>
              The invigilator cannot answer questions about test content.
            </li>
          </ul>

          <p style={bodyText}>
            Click the <strong>Help</strong> button (bottom left of the screen) during testing to refer to this guidance again. Test time will continue to count down while using the Help button.
          </p>
          <p style={{ ...bodyText, marginBottom: 0 }}>
            Please click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button to proceed before the time on this screen expires.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '14px 28px',
          borderTop: `1px solid ${divider}`,
          background: scheme.chrome,
        }}
      >
        <button
          type="button"
          onClick={onNext}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: ink,
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '8px 20px',
            cursor: 'pointer',
            fontFamily: 'var(--iv-font-body)',
          }}
        >
          <span>
            <span style={{ textDecoration: 'underline' }}>N</span>ext
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
};

/** Dispatcher: Pearson chrome iff pearsonSitting; else today's cream chrome (unchanged for orphans/fail-closed). */
const UcatRulesScreen = ({ pearsonSitting = false, ...rest }) => (
  pearsonSitting
    ? <UcatPearsonRulesScreen {...rest} />
    : <UcatCreamRulesScreen {...rest} />
);

export default UcatRulesScreen;
